import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { PrismicRichText } from '@prismicio/react'
import type { Address, Subscription } from '@rechargeapps/storefront-client'

import boxConfigurations from '@data/boxConfigurations.json'

import type { BoxConfiguration } from '@lib/types/box-configuration'
import { filterBonusGroups } from '@lib/customization/bonus-items'
import { resolveRichTextDateTokens } from '@lib/utils'
import useCustomization from '@lib/customization/use-customization'
import { getProductSamplesFromCollection } from '@lib/operations/collection'
import type { ShopifyBasicOrder } from '@lib/queries/orders/get-basic-orders-query'
import type { CollectionProductSample } from '@lib/types'

import CustomizationActionBar from './CustomizationActionBar'
import CustomizationModalProgress from './CustomizationProgress'
import CustomizationCardGroup from './customizationLayout/CustomizationCardGroup'
import SubmitCustomizationModal from './customizationLayout/SubmitCustomizationModal'
import CustomizationReview from './reviewLayout/CustomizationReviewCardGroup'

type CustomizationModalProps = {
	isModalOpen: boolean
	closeModal: () => void
	subscription?:
		| (Subscription & {
				address: Address
		  })
		| null
	revalidateChargeOrder: () => Promise<void>
	/** When clearing stale order attributes, use this so the modal stays open. Falls back to revalidateChargeOrder if not provided. */
	revalidateOnly?: () => Promise<void>
	shopifyOrder: ShopifyBasicOrder
	customizationType: 'NMC' | 'RMC'
	subscriptionProductSKU: string
	/** When true, disables submission and shows a preview-mode notice instead of saving. */
	isPreview?: boolean
}

export default function CustomizationModal({
	isModalOpen,
	closeModal,
	subscription,
	shopifyOrder,
	revalidateChargeOrder,
	revalidateOnly,
	customizationType,
	subscriptionProductSKU,
	isPreview
}: CustomizationModalProps) {
	const [step, setStep] = useState(0)
	const [customizationProductSamples, setCustomizationProductSamples] = useState<
		CollectionProductSample[]
	>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isRefreshingAfterClear, setIsRefreshingAfterClear] = useState(false)

	const {
		selections,
		bonusItemSelections,
		bonusItemConfig,
		addSelectionToStep,
		deleteSelectionToStep,
		loading: customizationLoading,
		reviewSubmitted,
		submitSelections,
		restoreSelectionsFromCustomAttributes
	} = useCustomization(customizationType, subscriptionProductSKU)

	const router = useRouter()
	const currentSelection = selections ? selections[step] : null

	// Reset selections when subscriptionProductSKU or customizationType changes
	useEffect(() => {
		setStep(0)
		setCustomizationProductSamples([])
	}, [subscriptionProductSKU, customizationType])

	// Logic to prevent scrolling on the body so that there is no double scroll.
	useEffect(() => {
		if (isModalOpen) {
			document.body.classList.add('no-scroll')
		} else {
			document.body.classList.remove('no-scroll')
		}

		// Cleanup function to remove the class when the component unmounts
		return () => {
			document.body.classList.remove('no-scroll')
		}
	}, [isModalOpen])

	useEffect(() => {
		let cancelled = false
		if (shopifyOrder.customAttributes.length > 0) {
			restoreSelectionsFromCustomAttributes(shopifyOrder.customAttributes, shopifyOrder.id).then(
				(result) => {
					if (cancelled || !result.hasStaleAttributes) return
					setIsRefreshingAfterClear(true)
					fetch('/api/shopify/box-clear-stale-customization', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ orderId: shopifyOrder.id })
					}).then(() => (revalidateOnly ?? revalidateChargeOrder)())
				}
			)
		}
		return () => {
			cancelled = true
		}
	}, [shopifyOrder.customAttributes, shopifyOrder.id, revalidateChargeOrder, revalidateOnly])

	// Hide "refreshing" loader once refetched order has empty customAttributes
	useEffect(() => {
		if (isRefreshingAfterClear && shopifyOrder.customAttributes.length === 0) {
			setIsRefreshingAfterClear(false)
		}
	}, [isRefreshingAfterClear, shopifyOrder.customAttributes.length])

	useEffect(() => {
		if (!isReviewStep) {
			updateCustomizationProductSamples()
		}
	}, [step])

	const boxConfiguration = (boxConfigurations as unknown as BoxConfiguration[]).find((config) =>
		config.product_skus.some((sku) => sku.sku === subscriptionProductSKU)
	)

	if (!boxConfiguration) {
		console.error(`No box configuration found for SKU: ${subscriptionProductSKU}`)
		return null
	}

	// Filter out bonus items from customization steps (they should not appear as steps)
	const allCustomizationGroups =
		customizationType === 'NMC'
			? boxConfiguration.nmc.customization_groups
			: boxConfiguration.rmc.customization_groups
	const customizationSteps = filterBonusGroups(allCustomizationGroups as any[])

	const customizationStepsHeader = resolveRichTextDateTokens(
		customizationType === 'NMC'
			? boxConfiguration.nmc.steps_header
			: boxConfiguration.rmc.steps_header
	)

	const customizationReviewsDescription =
		customizationType === 'NMC'
			? boxConfiguration.nmc.review_description
			: // @ts-ignore
				boxConfiguration.rmc.review_description || ''

	const postSubmitRedirect = (
		customizationType === 'NMC'
			? boxConfiguration.nmc.post_submit_redirect
			: boxConfiguration.rmc.post_submit_redirect
	) as 'Members Market' | null

	const customizationStep = customizationSteps[step]
	const isReviewStep = step >= customizationSteps.length - 1

	const submitSelectionsAndCloseModal = async () => {
		if (isPreview) {
			alert('Preview mode — submission is disabled. No changes have been saved.')
			return
		}
		await submitSelections(shopifyOrder.id)
		console.log('Submitting selections and closing modal - revalidateChargeOrder')
		setTimeout(() => {
			revalidateChargeOrder()
			if (postSubmitRedirect && postSubmitRedirect === 'Members Market') {
				router.push('/members-market')
			} else {
				closeModal()
			}
		}, 3000)
	}

	const updateCustomizationProductSamples = async () => {
		setIsLoading(true)
		try {
			// Get tolerance from Prismic configuration, fallback to legacy defaults
			const configTolerance =
				customizationType === 'NMC'
					? boxConfiguration.nmc.stock_tolerance
					: boxConfiguration.rmc.stock_tolerance

			// Default to 5 if not configured in Prismic
			const stockTolerance = configTolerance ?? 5
			// Fetch customization products based on the current step
			const productSamples = await getProductSamplesFromCollection(
				customizationStep.collection_slug,
				customizationType,
				stockTolerance
			)
			setCustomizationProductSamples(productSamples)
			console.log('Updating product samples:', productSamples)
		} catch (error) {
			console.error('Error fetching customization products:', error)
			setCustomizationProductSamples([])
		} finally {
			setIsLoading(false)
		}
	}

	const closeModalOnExitCustomization = async () => {
		await revalidateChargeOrder()

		// workaround to ensure the selections are restored after the customization modal is exited
		setTimeout(() => {
			restoreSelectionsFromCustomAttributes(shopifyOrder.customAttributes, shopifyOrder.id)
		}, 2000)
		closeModal()
	}

	if (!isModalOpen) {
		return null
	}

	return (
		<div
			id="customizationModal"
			className="fixed inset-0 pt-16 overflow-y-auto flex items-center flex-col z-50 bg-[#FDF1E3] pb-[100px] px-2"
			tabIndex={-1}
			role="dialog"
			aria-modal="true"
		>
			{(isLoading || customizationLoading || isRefreshingAfterClear) && (
				<div
					className="bg-[#FDF1E3]/90 h-full fixed inset-0 overflow-y-auto flex items-center flex-col z-[4000]"
					role="dialog"
					aria-modal="true"
				>
					<div className="m-auto text-center">
						{/* Add to prismic */}
						<div className="max-w-full flex flex-col justify-center items-center">
							<div className="border-gray-300 h-32 w-32 animate-spin rounded-full border-[10px] border-t-[#FF5C39]" />
						</div>
						<p className="text-[16px] mt-4 w-32">Saving your choices...</p>
					</div>
				</div>
			)}
			{/* header */}
			<div className="w-full max-w-[600px] text-center mb-16">
				{/* Add to prismic */}
				<h2 className="text-3xl xl:text-4xl title mb-[30px]">
					<PrismicRichText field={customizationStepsHeader as any} />
				</h2>

				{/* Progress bar for steps */}
				<CustomizationModalProgress
					currentStep={step}
					customizationType={customizationType}
					subscriptionProductSKU={subscriptionProductSKU}
				/>

				{/* Add to prismic */}
				<h3 className="mt-4 lg:mt-6 text-[18px] max-sm:text-[16px]">
					{isReviewStep && <PrismicRichText field={customizationReviewsDescription as any} />}
					{!isReviewStep && (
						<>
							<strong>{`Select ${selections[step]?.maxItems}`}</strong>{' '}
							{selections[step]?.maxItems === 1 ? 'product' : 'products'} from this group
						</>
					)}
				</h3>
			</div>

			{/* choose items section */}
			{!isReviewStep && (
				<CustomizationCardGroup
					productSamples={customizationProductSamples}
					currentStep={step}
					selections={selections}
					addSelectionToStep={addSelectionToStep}
					deleteSelectionToStep={deleteSelectionToStep}
				/>
			)}

			{/* review section */}
			{isReviewStep && (
				<CustomizationReview
					shippingInfo={subscription?.address}
					selections={selections}
					bonusItemSelections={bonusItemSelections}
					updateStep={setStep}
					submitSelections={submitSelectionsAndCloseModal}
					customizationType={customizationType}
					subscriptionProductSKU={subscriptionProductSKU}
					bonusItemConfig={bonusItemConfig}
				/>
			)}

			{/* action bar */}
			<CustomizationActionBar
				closeModal={closeModalOnExitCustomization}
				currentStep={step}
				setStep={setStep}
				currentSelection={currentSelection}
				submitSelections={submitSelectionsAndCloseModal}
				customizationType={customizationType}
				subscriptionProductSKU={subscriptionProductSKU}
			/>

			{reviewSubmitted && <SubmitCustomizationModal isSubmitting={customizationLoading} />}
		</div>
	)
}
